import { useEffect, useState } from "react";
import { Box, Button, Stack } from "@vapor/react-material";
import Modal from "@vapor/react-material/Modal";
import { modalStyleLarge } from "../businessLogic/styles";
import { useTranslation } from "@1f/react-sdk";
import TextField from "@vapor/react-material/TextField";
import Autocomplete from "@vapor/react-material/Autocomplete";
import Checkbox from "@vapor/react-material/Checkbox";
import FormControlLabel from "@vapor/react-material/FormControlLabel";
import ExtendedTypography from "@vapor/react-extended/ExtendedTypography";
import { useSnackbar } from "notistack";

const SIGNATURE_OPTIONS = [
    {
        id: "feq-remota-otp",
        label: "FEQ remota",
        signatureType: "FEQ remota",
        requiredCertificateType: "FSC",
    },
    {
        id: "feq-dispositivo-fisico",
        label: "FEQ con dispositivo fisico",
        signatureType: "FEQ con dispositivo fisico",
        requiredCertificateType: "FSC",
    },
    {
        id: "fea-remota-otp",
        label: "FEA remota",
        signatureType: "FEA remota",
        requiredCertificateType: "FEA",
    },
];

const isCertificateAvailable = (certificate) =>
    Boolean(certificate) && certificate.enabled !== false && certificate.signatureFlowEnabled !== false;

const getAvailableSignatureOptions = (signer) => {
    const certificateTypes = new Set(
        (signer?.certificates || [])
            .filter(isCertificateAvailable)
            .map((certificate) => certificate.type)
    );

    return SIGNATURE_OPTIONS.filter((option) => certificateTypes.has(option.requiredCertificateType));
};

const getCertificateForSignature = (signer, signatureOption) => {
    if (!signatureOption) return null;

    return (
        (signer?.certificates || []).find(
            (certificate) =>
                isCertificateAvailable(certificate) &&
                certificate.type === signatureOption.requiredCertificateType
        ) || null
    );
};

export const ModalSignature = ({ show, onOk, onClose, signerList, currentAllegato }) => {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const [selectedSigner, setSelectedSigner] = useState(null);
    const [selectedSignature, setSelectedSignature] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [useSignatureTimestamp, setUseSignatureTimestamp] = useState(false);

    // Initialize defaults when modal opens
    useEffect(() => {
        if (!show) {
            setIsSubmitting(false);
            return;
        }

        setIsSubmitting(false);

        const firstSigner = signerList.find(
            (signer) => signer?.enabled && getAvailableSignatureOptions(signer).length > 0
        );

        if (!firstSigner) {
            setSelectedSigner(null);
            setSelectedSignature(null);
            setUseSignatureTimestamp(false);
            return;
        }

        const firstSignature = getAvailableSignatureOptions(firstSigner)[0] || null;
        setSelectedSigner(firstSigner);
        setSelectedSignature(firstSignature);
        setUseSignatureTimestamp(false);
    }, [show, signerList, currentAllegato]);

    // Update CognomeNomeRiferimento and CodiceFiscaleRiferimento when signer changes — removed (CCT handled in ModalConservazione)

    const availableSignatures = selectedSigner ? getAvailableSignatureOptions(selectedSigner) : [];
    const selectedCertificate = getCertificateForSignature(selectedSigner, selectedSignature);
    const isConfirmDisabled = !selectedSigner || !selectedSignature || !selectedCertificate;

    const handleOk = async (e) => {
        if (isConfirmDisabled || isSubmitting) return;

        setIsSubmitting(true);

        try {
            await onOk({
                event: e,
                signerId: selectedSigner.id,
                certificateId: selectedCertificate.id,
                signer: selectedSigner,
                certificate: selectedCertificate,
                signature: selectedSignature,
                autoSendCCT: false,
                useSignatureTimestamp,
                metadata: {},
            });
        } catch (error) {
            setIsSubmitting(false);
            throw error;
        }
    };

    const handleCancel = (e) => {
        onClose(e);
    };

    return (
        <Modal
            key="modal-signature"
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description"
            open={show}
            onOk={handleOk}
            onCancel={handleCancel}
            onClose={handleCancel}
        >
            <Box sx={{ ...modalStyleLarge, display: "flex", flexDirection: "column" }}>
                {/* Header */}
                <Box sx={{ px: 3, pt: 2.5, pb: 2, borderBottom: "1px solid", borderColor: "divider" }}>
                    <ExtendedTypography variant="titleSmall" color="primary.main">
                        Invio in firma
                    </ExtendedTypography>
                    {currentAllegato?.displayedName && (
                        <ExtendedTypography variant="body2" sx={{ mt: 0.5, color: "text.secondary" }}>
                            {currentAllegato.displayedName}
                        </ExtendedTypography>
                    )}
                </Box>

                {/* Body */}
                <Stack spacing={2.5} sx={{ px: 3, py: 3 }}>
                    <Autocomplete
                        id="signature-signer"
                        fullWidth
                        size="small"
                        options={signerList}
                        value={selectedSigner}
                        getOptionLabel={(option) =>
                            `${option.firstName || ""} ${option.lastName || ""} — ${option.maskedCompleteNumber || ""}`.trim()
                        }
                        getOptionDisabled={(option) =>
                            !option.enabled || getAvailableSignatureOptions(option).length === 0
                        }
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        onChange={(_, newValue) => {
                            setSelectedSigner(newValue);
                            if (newValue) {
                                setSelectedSignature(getAvailableSignatureOptions(newValue)[0] || null);
                            } else {
                                setSelectedSignature(null);
                            }
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Firmatario"
                                required
                                size="small"
                            />
                        )}
                        noOptionsText="Nessun firmatario disponibile"
                    />

                    <Autocomplete
                        id="signature-type"
                        fullWidth
                        size="small"
                        options={availableSignatures}
                        value={selectedSignature}
                        getOptionLabel={(option) => option.label}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        onChange={(_, newValue) => setSelectedSignature(newValue)}
                        disabled={!selectedSigner}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Tipo di firma"
                                required
                                size="small"
                            />
                        )}
                        noOptionsText="Nessuna firma disponibile"
                    />

                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={useSignatureTimestamp}
                                onChange={(e) => setUseSignatureTimestamp(e.target.checked)}
                                size="small"
                            />
                        }
                        label={
                            <ExtendedTypography variant="body2">
                                Apponi marca temporale
                            </ExtendedTypography>
                        }
                    />
                </Stack>

                {/* Footer */}
                <Stack
                    direction="row"
                    spacing={1}
                    sx={{ px: 3, py: 2, mt: "auto", bgcolor: "#F2F5F8", borderTop: "1px solid", borderColor: "divider" }}
                    justifyContent="flex-end"
                    alignItems="center"
                >
                    <Button variant="outlined" size="small" onClick={handleCancel}>
                        {t("actions.annulla")}
                    </Button>
                    <Button variant="contained" size="small" onClick={handleOk} disabled={isConfirmDisabled || isSubmitting}>
                        Invia in firma
                    </Button>
                </Stack>
            </Box>
        </Modal>
    );
};